import { colorPalette, visibleCarCaps } from "../../design/productConfig";
import { mockLocationFor } from "../../features/location/coarseLocation";
import {
  LANES,
  VEHICLE_PRESETS,
  type Lane,
  type PerformanceTier,
  type RemoteDriver,
  type TrafficEvent,
} from "../../types/traffic";
import type {
  CohortConnectionInput,
  RealtimeListener,
  TrafficRealtimeProvider,
} from "./TrafficRealtimeProvider";

const colorIds = Object.keys(colorPalette);

function hash(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

function itemAt<T>(items: readonly T[], index: number): T {
  const normalizedIndex =
    ((index % items.length) + items.length) % items.length;
  const item = items[normalizedIndex];
  if (item === undefined) throw new Error("Cannot select from an empty list.");
  return item;
}

export function createMockDrivers(
  requestedCount: number,
  tier: PerformanceTier,
): RemoteDriver[] {
  const visibleCount = Math.min(
    Math.max(0, requestedCount),
    visibleCarCaps[tier],
  );

  return Array.from({ length: visibleCount }, (_, index) => {
    const seed = hash(`traffic-${index}`);
    const plateNumber = String((seed % 900) + 100);
    return {
      sessionId: `mock-session-${String(index).padStart(3, "0")}`,
      publicDriverId: `mock-driver-${String(index).padStart(3, "0")}`,
      lane: itemAt(LANES, seed),
      joinedAt: new Date(Date.now() - (seed % 90_000)).toISOString(),
      vehiclePreset: itemAt(VEHICLE_PRESETS, seed >> 4),
      colorId: itemAt(colorIds, seed >> 8),
      plate: `JAM-${plateNumber}`,
      badge: index === 2 ? "founding" : null,
      sessionElapsedSeconds: seed % 7_200,
      sessionDistanceMiles: (seed % 420) / 10,
      location: mockLocationFor(`traffic-${index}`),
    };
  });
}

export class MockTrafficRealtimeProvider implements TrafficRealtimeProvider {
  private readonly listeners = new Set<RealtimeListener>();
  private connectedInput: CohortConnectionInput | null = null;

  public constructor(
    private tier: PerformanceTier,
    private driverCount = 24,
  ) {}

  public connect(input: CohortConnectionInput): Promise<void> {
    this.emit({ type: "connection", state: "connecting" });
    this.connectedInput = input;
    this.emit({
      type: "snapshot",
      drivers: createMockDrivers(Math.max(0, this.driverCount - 1), this.tier),
    });
    this.emit({ type: "connection", state: "connected" });
    return Promise.resolve();
  }

  public reconnect(input: CohortConnectionInput): Promise<void> {
    return this.connect(input);
  }

  public updateLane(lane: Lane): Promise<void> {
    if (this.connectedInput) {
      this.connectedInput = {
        ...this.connectedInput,
        driver: { ...this.connectedInput.driver, lane },
      };
    }
    return Promise.resolve();
  }

  public updatePresence(driver: RemoteDriver): Promise<void> {
    if (this.connectedInput) {
      this.connectedInput = { ...this.connectedInput, driver };
    }
    return Promise.resolve();
  }

  public publish(event: TrafficEvent): Promise<void> {
    this.emit({ type: "event", event });
    return Promise.resolve();
  }

  public disconnect(): Promise<void> {
    this.connectedInput = null;
    this.emit({ type: "connection", state: "offline" });
    return Promise.resolve();
  }

  public isConnected(): boolean {
    return this.connectedInput !== null;
  }

  public subscribe(listener: RealtimeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  public setDriverCount(driverCount: number): void {
    this.driverCount = driverCount;
    const localDriverOffset = this.connectedInput ? 1 : 0;
    this.emit({
      type: "snapshot",
      drivers: createMockDrivers(
        Math.max(0, driverCount - localDriverOffset),
        this.tier,
      ),
    });
  }

  public setPerformanceTier(tier: PerformanceTier): void {
    this.tier = tier;
    this.setDriverCount(this.driverCount);
  }

  private emit(update: Parameters<RealtimeListener>[0]): void {
    this.listeners.forEach((listener) => listener(update));
  }
}
